// Typed client for /pipelines (LT-143).

import axios from 'axios';
import {
  Pipeline,
  PipelineCreate,
  PipelineStage,
  PipelineStageCreate,
  PipelineStageUpdate,
  PipelineUpdate,
} from '../types/pipeline';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const pipelinesAPI = {
  list: async (): Promise<Pipeline[]> => {
    const r = await client.get<Pipeline[]>('/pipelines/');
    return r.data;
  },

  get: async (id: string): Promise<Pipeline> => {
    const r = await client.get<Pipeline>(`/pipelines/${id}`);
    return r.data;
  },

  create: async (payload: PipelineCreate): Promise<Pipeline> => {
    const r = await client.post<Pipeline>('/pipelines/', payload);
    return r.data;
  },

  update: async (id: string, patch: PipelineUpdate): Promise<Pipeline> => {
    const r = await client.patch<Pipeline>(`/pipelines/${id}`, patch);
    return r.data;
  },

  remove: async (id: string): Promise<void> => {
    await client.delete(`/pipelines/${id}`);
  },

  listStages: async (pipelineId: string): Promise<PipelineStage[]> => {
    const r = await client.get<PipelineStage[]>(
      `/pipelines/${pipelineId}/stages`
    );
    return r.data;
  },

  addStage: async (
    pipelineId: string,
    payload: PipelineStageCreate
  ): Promise<PipelineStage> => {
    const r = await client.post<PipelineStage>(
      `/pipelines/${pipelineId}/stages`,
      payload
    );
    return r.data;
  },

  renameStage: async (
    pipelineId: string,
    stageId: string,
    patch: PipelineStageUpdate
  ): Promise<PipelineStage> => {
    const r = await client.patch<PipelineStage>(
      `/pipelines/${pipelineId}/stages/${stageId}`,
      patch
    );
    return r.data;
  },

  removeStage: async (pipelineId: string, stageId: string): Promise<void> => {
    await client.delete(`/pipelines/${pipelineId}/stages/${stageId}`);
  },

  reorderStages: async (
    pipelineId: string,
    orderedStageIds: string[]
  ): Promise<PipelineStage[]> => {
    const r = await client.post<PipelineStage[]>(
      `/pipelines/${pipelineId}/stages/reorder`,
      { ordered_stage_ids: orderedStageIds }
    );
    return r.data;
  },
};
